import React, { forwardRef, useState, useImperativeHandle } from 'react';
import { 
  TextField, 
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import {
  TextMaskCreditCard,
  TextMaskCreditExpiration,
  TextMaskCreditCardCVV 
} from '../components/TextMaskCustom';
import CreditCard from '../components/CreditCard';
import valid from 'card-validator';
import PropTypes from 'prop-types';

const COLORS = {
  primary: '#012F47',
  secondary: '#239CC4',
  white: '#FFFFFF',
};

const commonBoxStyles = {
  maxWidth: { xs: '100%', lg: '1200px' },
  mx: 'auto',
  bgcolor: COLORS.white,
  borderRadius: { xs: 2, sm: 3, md: 4 },
  boxShadow: '0px 6px 24px 0px #012F473A',
};

const CardForm = forwardRef((props, ref) => {
  const { card, setCard, errors, setErrors } = props;

  const handleCardInputFocus = (e) => {
    setCard({...card, focus: e.target.name });
  };

  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === 'name') {
      nextValue = value
        .replace(/[^a-zA-Z\s]/g, '')
        .replace(/\s+/g, ' ');
    }

    const newCard = { ...card, [name]: nextValue };
    setCard(newCard);

    const fieldError = validateCard(newCard);
    setErrors((prev) => {
      const next = { ...prev };
      if (fieldError[name]) {
        next[name] = fieldError[name];
      } else {
        delete next[name];
      }
      return next;
    });
  }; 
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#EDF2F5',
      borderRadius: '9.15px',
      transition: 'transform 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        transform: 'translateY(-2px)',
        '& fieldset': {
          borderColor: '#012F47',
          borderWidth: '1px',
        }
      },
      '&.Mui-focused': {
        transform: 'translateY(-2px)',
        '& fieldset': {
          borderColor: '#012F47',
          borderWidth: '1px',
        }
      },
    },
    '& label.Mui-focused': {
      color: '#536777',
    },
    '& .MuiInputLabel-root': {
      color: '#536777',
      fontSize: '0.9rem',
    },
    '& .MuiOutlinedInput-input': {
      color: '#012F47',
      padding: '14px 16px',
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px',
      fontSize: '0.75rem',
    },
  };
  
  const validateCard = (card) => {
    const errors = {};

    // Validação do nome (mantida simples)
    if (!card.name) {
      errors.name = 'Nome é obrigatório';
    } else if (card.name.length < 3) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (!/^[a-zA-Z\s]*$/.test(card.name)) {
      errors.name = 'Nome deve conter apenas letras';
    }

    // Validação do número do cartão (usando apenas a lib)
    const numberValidation = valid.number(card.number);
    if (!numberValidation.isValid) {
      errors.number = 'Número do cartão inválido';
    }

    // Validação da data de expiração (usando apenas a lib)
    const expiryValidation = valid.expirationDate(card.expiry);
    if (!expiryValidation.isValid) {
      errors.expiry = 'Data de validade inválida';
    }

    // Validação do CVV (usando apenas a lib)
    const cvvValidation = valid.cvv(card.cvv);
    if (!cvvValidation.isValid) {
      errors.cvv = 'CVV inválido';
    }

    return errors;
  };


  const validate = () => {
    const newErrors = {};
    
    if (!card?.number) {
      newErrors.number = 'Número do cartão é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate
  }));

  return (
    <Box sx={{ 
      ...commonBoxStyles,
      p: { xs: 5, sm: 5, md: 10 },
      pt: { xs: 5, sm: 5, md: 5 },
      mb: 5
    }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
      }}>
        <Typography 
          variant="h1" 
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '22px', md: '30px' },
            color: COLORS.primary,
          }}
        >
          Dados do Cartão
        </Typography>

        <Grid 
          container 
          spacing={{ xs: 2, md: 4 }} 
          alignItems="center"
          justifyContent="center"
        >
          {/* Coluna do Cartão - Agora sempre aparece primeiro em mobile */}
          <Grid 
            item 
            xs={12} 
            md={6}
            sx={{
              order: { xs: -1, md: 0 },
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 2, md: 0 },
            }}
          >
            <CreditCard card={card}/>
          </Grid>

          {/* Coluna dos Inputs */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            container 
            spacing={2}
            sx={{
              maxWidth: { xs: '100%', sm: '500px', md: '100%' },
            }}
          >
            <Grid item xs={12}>
              <TextField
                id="card-name"
                label="Nome do titular do cartão"
                name="name"
                variant="outlined"
                sx={textFieldSx}
                value={card.name}
                error={Boolean(errors.name)}
                helperText={errors.name}
                onChange={handleCardInputChange}
                onFocus={handleCardInputFocus}
                autoComplete="off"
                fullWidth
                inputProps={{
                  maxLength: 40,
                  pattern: '[A-Za-z ]*',
                  style: { textTransform: 'uppercase' } 
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                id="card-number"
                name="number"
                label="Número do cartão"
                variant="outlined"
                type="tel"
                sx={textFieldSx}
                value={card.number}
                error={Boolean(errors.number)}
                helperText={errors.number}
                onChange={handleCardInputChange}
                onFocus={handleCardInputFocus}
                InputProps={{ 
                  inputComponent: TextMaskCreditCard,
                  maxLength: 19,
                }}
                autoComplete="off"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="card-expiry"
                label="Data de vencimento"
                name="expiry"
                variant="outlined"
                sx={textFieldSx}
                value={card.expiry}
                error={Boolean(errors.expiry)}
                helperText={errors.expiry}
                onChange={handleCardInputChange}
                onFocus={handleCardInputFocus}
                InputProps={{ 
                  inputComponent: TextMaskCreditExpiration,
                  maxLength: 5
                }}
                autoComplete="off"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="card-cvv"
                label="CVV"
                name="cvv"
                type="tel"
                variant="outlined"
                sx={textFieldSx}
                value={card.cvv}
                error={Boolean(errors.cvv)}
                helperText={errors.cvv}
                onChange={handleCardInputChange}
                onFocus={handleCardInputFocus}
                InputProps={{
                  inputComponent: TextMaskCreditCardCVV,
                  maxLength: 4,
                }}
                autoComplete="off"
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

CardForm.propTypes = {
  card: PropTypes.shape({
    number: PropTypes.string,
    name: PropTypes.string,
    expiry: PropTypes.string,
    cvv: PropTypes.string,
    focus: PropTypes.string
  }).isRequired,
  setCard: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired
};

export default CardForm;